import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo } from '@/mocks/mangaData';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于禁漫天 - 品牌介绍',
  description: brandInfo.description,
  url: 'https://jinmantian.com/about',
  about: {
    '@type': 'Organization',
    name: '禁漫天',
    foundingDate: '2018',
    description: brandInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      email: brandInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  },
  inLanguage: 'zh-CN',
};

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于禁漫天 - 品牌介绍、口碑与联系方式';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      {/* Header */}
      <div className="pt-20 bg-background-100 border-b border-background-200/60">
        <Breadcrumb
          items={[{ label: '首页', path: '/' }, { label: '关于我们' }]}
        />

        <div className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于禁漫天
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              国内领先的漫画阅读平台，自{brandInfo.founded}成立以来，始终致力于为漫画爱好者提供优质服务
            </p>
          </div>
        </div>
      </div>

      {/* Brand Introduction */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            {/* Image */}
            <div className="rounded-lg overflow-hidden h-72 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20for%20digital%20manga%20publishing%2C%20team%20collaboration%20on%20comic%20creation%2C%20warm%20and%20inviting%20atmosphere%2C%20colorful%20manga%20art%20on%20walls%2C%20professional%20yet%20creative%20environment%2C%20Japanese%20aesthetic%20mixed%20with%20modern%20office%2C%20soft%20natural%20lighting%2C%20warm%20coral%20and%20cream%20tones&width=800&height=600&seq=about-brand-image&orientation=landscape"
                alt="禁漫天品牌形象"
                title="禁漫天品牌形象"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Text */}
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {brandInfo.description}
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                <strong>我们的使命：</strong>{brandInfo.mission}
              </p>

              {/* Values */}
              <div className="flex flex-wrap gap-3 mb-6">
                {brandInfo.values.map((v) => (
                  <span
                    key={v}
                    className="inline-block px-4 py-2 bg-primary-50 text-primary-600 text-sm font-medium rounded-full"
                  >
                    {v}
                  </span>
                ))}
              </div>

              <Link
                to="/article/1"
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer"
              >
                访问官方入口
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
            平台数据
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {brandInfo.achievements.map((item) => (
              <div
                key={item.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                  {item.value}
                </div>
                <div className="text-sm text-foreground-600">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {brandInfo.testimonials.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 border border-background-200/70 rounded-lg p-6"
              >
                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-4">
                  {Array.from({ length: item.rating }, (_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "{item.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-bold text-sm">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                    <div className="text-xs text-foreground-500">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
            联系我们
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.email}</p>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                <i className="ri-weibo-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">官方微博</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.weibo}</p>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
                <i className="ri-wechat-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.wechat}</p>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 flex items-start gap-4">
              <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className="ri-time-line text-lg"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服时间</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.hours}</p>
              </div>
            </div>
          </div>

          {/* Map placeholder */}
          <div className="mt-10 bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-map-pin-line text-xl"></i>
            </div>
            <p className="text-sm text-foreground-600">{brandInfo.contact.address}</p>
            <p className="text-xs text-foreground-400 mt-1">禁漫天运营团队</p>
          </div>
        </div>
      </section>
    </div>
  );
}